import React, { useState } from "react";
import {
  useCreateConsoleWebsiteMutation,
  useUpdateConsoleWebsiteMutation,
} from "../../app/api/console/consoleWebsitesApiSlice";
import { useCreateWebsiteMutation } from "../../app/api/platform/platformWebsitesApiSlice";
import { useUploadImageMutation } from "../../app/api/console/consoleImagesApiSlice";

const inputClass = "w-full px-3 py-2.5 text-body-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40 transition-all";

function CreateWebsiteModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({ website_url: "", website_name: "", logo_url: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const [createConsoleWebsite, { isLoading }] = useCreateConsoleWebsiteMutation();
  const [updateConsoleWebsite] = useUpdateConsoleWebsiteMutation();
  const [createPlatformWebsite] = useCreateWebsiteMutation();
  const [uploadImage] = useUploadImageMutation();

  const extractNameFromUrl = (url) => {
    try {
      const hostname = new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "");
      const name = hostname.split(".")[0] || "";
      return name.charAt(0).toUpperCase() + name.slice(1);
    } catch { return ""; }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "website_url") {
      setFormData((prev) => ({ ...prev, [name]: value, website_name: extractNameFromUrl(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleLogoFile = async (file) => {
    if (!file || !file.type.match("image.*")) {
      setErrors((prev) => ({ ...prev, logo_url: "Please select an image file" }));
      return;
    }
    if (errors.logo_url) setErrors((prev) => ({ ...prev, logo_url: undefined }));
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setIsUploadingLogo(true);
    try {
      const result = await uploadImage({ file, useProd: false }).unwrap();
      setFormData((prev) => ({ ...prev, logo_url: result.url }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, logo_url: error.data?.detail || "Failed to upload logo" }));
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) handleLogoFile(file);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.website_url.trim()) newErrors.website_url = "Website URL is required";
    else if (!formData.website_url.startsWith("http")) newErrors.website_url = "URL must start with http:// or https://";
    if (!formData.website_name.trim()) newErrors.website_name = "Website name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setStatusMessage("Creating website...");
    try {
      const consoleResult = await createConsoleWebsite({ name: formData.website_name }).unwrap();

      setStatusMessage("Setting up staging...");
      const stagingResult = await createPlatformWebsite({
        website: { website_name: formData.website_name, website_url: formData.website_url, logo_url: formData.logo_url || null },
        useProd: false,
      }).unwrap();

      setStatusMessage("Setting up production...");
      let productionResult = null;
      try {
        productionResult = await createPlatformWebsite({
          website: { website_name: formData.website_name, website_url: formData.website_url, logo_url: formData.logo_url || null },
          useProd: true,
        }).unwrap();
      } catch { /* prod creation is best-effort */ }

      setStatusMessage("Linking environments...");
      try {
        await updateConsoleWebsite({
          websiteId: consoleResult.console_website_id,
          website: { name: formData.website_name, staging_website_id: stagingResult.website_id, prod_website_id: productionResult?.website_id || null },
        }).unwrap();
      } catch { /* linking is best-effort */ }

      setStatusMessage("Done!");
      setTimeout(() => handleClose(), 1000);
    } catch (error) {
      setStatusMessage("");
      setErrors((prev) => ({ ...prev, form: error.data?.detail || "Failed to create website" }));
    }
  };

  const handleClose = () => {
    setFormData({ website_url: "", website_name: "", logo_url: "" });
    setLogoFile(null);
    setLogoPreview(null);
    setErrors({});
    setStatusMessage("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl border border-border-subtle p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-body-lg font-semibold text-text-primary">Create New Website</h2>
          <button onClick={handleClose} className="text-text-tertiary hover:text-text-secondary transition-colors">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1.5">Website URL *</label>
            <input
              name="website_url"
              type="text"
              value={formData.website_url}
              onChange={handleChange}
              placeholder="https://example.com"
              className={`${inputClass} ${errors.website_url ? "border-severity-high/50" : "border-border-subtle"}`}
              disabled={isLoading}
            />
            {errors.website_url && <p className="mt-1 text-caption text-severity-high">{errors.website_url}</p>}
          </div>

          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1.5">
              Website Name * <span className="text-text-tertiary font-normal">(auto-extracted)</span>
            </label>
            <input
              name="website_name"
              type="text"
              value={formData.website_name}
              onChange={handleChange}
              className={`${inputClass} ${errors.website_name ? "border-severity-high/50" : "border-border-subtle"}`}
              disabled={isLoading}
            />
            {errors.website_name && <p className="mt-1 text-caption text-severity-high">{errors.website_name}</p>}
          </div>

          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1.5">Logo (optional)</label>
            <label
              className={`flex items-center gap-3 p-3 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                isDragging ? "border-accent-purple bg-accent-purple/5" : "border-border-subtle hover:border-accent-purple/40 hover:bg-surface-neutral"
              }`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); const file = e.dataTransfer.files[0]; if (file) handleLogoFile(file); }}
            >
              <div className="flex-shrink-0 h-10 w-10 bg-surface-neutral rounded-xl overflow-hidden flex items-center justify-center">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="h-full w-full object-cover" />
                ) : (
                  <svg className="h-5 w-5 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-body-xs font-medium text-accent-purple">
                  {isUploadingLogo ? "Uploading..." : logoFile ? "Change logo" : "Drop image or click to upload"}
                </p>
                <p className="text-caption text-text-tertiary">PNG, JPG, SVG</p>
              </div>
              <input type="file" accept="image/*" className="sr-only" onChange={handleLogoChange} disabled={isUploadingLogo || isLoading} />
            </label>
            {errors.logo_url && <p className="mt-1 text-caption text-severity-high">{errors.logo_url}</p>}
          </div>

          {errors.form && (
            <div className="rounded-xl bg-severity-high/5 border border-severity-high/20 px-3 py-2.5">
              <p className="text-body-xs text-severity-high">{errors.form}</p>
            </div>
          )}

          {statusMessage && (
            <div className="rounded-xl bg-accent-purple/5 border border-accent-purple/20 px-3 py-2.5">
              <p className="text-body-xs text-accent-purple font-medium">{statusMessage}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-body-xs font-medium text-text-secondary border border-border-default rounded-xl hover:bg-surface-neutral transition-colors"
              disabled={isLoading}>
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-body-xs font-semibold text-white bg-button-primary rounded-xl hover:bg-button-primary-hover disabled:opacity-40 transition-colors"
              disabled={isLoading || isUploadingLogo}>
              {isLoading ? "Creating..." : "Create Website"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateWebsiteModal;
